import { Heading } from '@radix-ui/themes';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { FormTextField } from 'components/form/textfield';
import { ActionSelect } from 'components/layout/action-select';
import { EB } from 'components/layout/error-boundary';
import { IssuerForm } from 'components/main/routes/issuers/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { BaseDoc } from 'lib/interfaces/database/_base';
import { IssuerWhole } from 'lib/interfaces/database/issuer';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useIssuersStore from 'stores/issuers';

export const Issuers = () => {
  const { data, create, load, update } = useIssuersStore();

  const { perms } = useContext(AuthContext);
  const { issuers: crud } = perms;

  useEffect(() => {
    if (crud?.read) {
      load();
    }
  }, [crud?.read, load]);

  const [dialog, setDialog] = useState<DialogState>();

  const [search, setSearch] = useState('');

  const filteredData = useMemo(() => {
    if (!search.trim()) {
      return data;
    }

    const lowerSearch = search.toLowerCase();

    return data.filter((d) => {
      if (d.name.toLowerCase().includes(lowerSearch)) {
        return true;
      }

      return false;
    });
  }, [search, data]);

  return (
    <EB>
      <Heading>Issuers</Heading>
      <CommonTable
        defaultSort={{
          iCol: 1,
          asc: false,
        }}
        toolbarActions={[
          {
            hidden: !crud?.create,
            label: 'Create',
            onClick: () =>
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              }),
          },
        ]}
        toolbarControls={[
          <FormTextField
            placeholder="Search"
            defaultValue={search}
            onChange={setSearch}
            className="width-250px"
            hideLabel
          />,
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: IssuerWhole) => (
              <ActionSelect
                options={[
                  {
                    hidden: !crud?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Update: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: IssuerWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: IssuerWhole, b: IssuerWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Name',
            rowFn: (item: IssuerWhole) => item.name,
            sortFn: (a: IssuerWhole, b: IssuerWhole) =>
              SortUtil.strings(a.name, b.name),
          },
        ]}
        data={filteredData}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<IssuerForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<IssuerForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => update((dialog.data as BaseDoc).id, item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}
    </EB>
  );
};
